import { Link, useMatchRoute } from '@tanstack/react-router';
import type { LucideIcon } from 'lucide-react';
import {
  LayoutDashboard, Building2, Users, Shield, Handshake,
  FileText, GitBranch, Settings, Star, BarChart3, Plug,
  Sparkles, ClipboardList, Briefcase, Landmark, Receipt, ClipboardCheck, Lock, Clock,
  Contact, FileSignature, FileUp, LifeBuoy,
  // Share2, AlertTriangle, // hidden for demo — restore when ready (Relationships, Risk)
} from 'lucide-react';
import { Text } from '@/components/ui/Text';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/Tooltip';
// import { useOrgRelationshipsEnabled } from '@/lib/org-relationships-flag'; // hidden for demo — restore when ready
import { usePersonaViewsEnabled } from '@/lib/persona-views-flag';
import { AclRoute, CrmAclAnyOf, GlobalAclKeys, AgencyAclKeys, HelpRequestAclKeys, usePermissions } from '@/lib/acl';
import { useContextStore } from '@/lib/session/context-store';
import { useAgencyStatus } from '@/pages/Agency/useAgencyStatus';
import { useDataTheme } from '@/lib/hooks/useDataTheme';
import { useCrmAccess } from '@/lib/crm/use-crm-access';
import { CrmSidebarNav } from './CrmSidebarNav';

/**
 * ACL-gated sidebar routes (ENT-89 T15). A nav item with a key here is shown only
 * when the caller holds that UI permission-key for the GLOBAL route — layered ON
 * TOP of the persona/flag gating below (AND, not replace). Fail-closed: hidden
 * while keys load / on error. UX only — the BE still authorizes every real call.
 */
const ACL_KEY_BY_ROUTE: Record<string, string> = {
  '/clients': GlobalAclKeys.sidebarClients,
  '/entities': GlobalAclKeys.sidebarEntities,
  '/workforce': GlobalAclKeys.sidebarWorkers,
  '/people': GlobalAclKeys.sidebarPeople,
  '/providers': GlobalAclKeys.sidebarVendors,
  '/agencies': GlobalAclKeys.sidebarAgencies,
  '/engagements': GlobalAclKeys.sidebarEngagements,
  '/agent': GlobalAclKeys.sidebarAgent,
  // Invoices is a dedicated tab for EVERY persona, so `sidebar:invoices` is seeded YES across all
  // five in `acl-svc/config/ui-permissions.csv`. It is gated here for consistency with its
  // siblings, not to restrict anyone — per-persona differences live in the page
  // (src/pages/Invoices/persona-config.ts), never in whether the tab renders.
  // ⚠️ This gate is FAIL-CLOSED: until that CSV row is imported, the tab is hidden from everyone.
  '/invoices': GlobalAclKeys.sidebarInvoices,
  // ⚠️ Same fail-closed caveat as `/invoices` above: hidden from everyone until
  // `GLOBAL,sidebar:time` is seeded in acl-svc's ui-permissions.csv.
  '/time': GlobalAclKeys.sidebarTime,
  // Import Center — `sidebar:import-center` is granted to SA only in the CSV (fail-closed until imported).
  '/import-center': GlobalAclKeys.sidebarImportCenter,
  '/integrations': GlobalAclKeys.sidebarIntegrations,
};

const mainNav = [
  // T0009 — renamed from "Control Tower" (owner-ruled: this is now THE Dashboard, replacing the
  // retired "My Dashboard" item below). Route stays '/' — the ask was the name, not the route.
  { to: '/' as const,           icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/agent' as const,      icon: Sparkles,        label: 'Ask Entiti' },
  { to: '/entities' as const,   icon: Building2,       label: 'Entities' },
  { to: '/import-center' as const, icon: FileUp,       label: 'Import Center' },
  { to: '/engagements' as const, icon: Landmark,       label: 'Incorporation' },
  { to: '/workforce' as const,  icon: Users,           label: 'Workers' },
  { to: '/compliance' as const, icon: Shield,          label: 'Compliance' },
  { to: '/time' as const,       icon: Clock,           label: 'Time' },
  { to: '/providers' as const,    icon: Handshake,     label: 'Vendors' },
  { to: '/invoices' as const,     icon: Receipt,       label: 'Invoices' },
  { to: '/integrations' as const, icon: Plug,          label: 'Integrations' },
  // ENT-565 ZDP-T37: Agency CRM proposals. Gated on the tenant's crm_core entitlement AND a proposals
  // list key on the CRM route (`useCrmAccess`), the same two gates crm-svc applies to the list call.
  { to: '/proposals' as const,    icon: FileSignature, label: 'Proposals' },
  // ENT-565: Proposals are mounted at both `/proposals` and `/crm/proposals` until the CRM is fully
  // wired; then this entry and the `/proposals` routes go. The CRM itself opens from the navbar's
  // tool switcher, not from this list. Inside it, `CrmSidebarNav` replaces this sidebar.
  { to: '/documents' as const,      icon: FileText,        label: 'Documents' },
  // CI-PHASE2: Help Requests. Gated on its OWN route's key (HELP_REQUEST / listingRead), the
  // exact key the page needs to show its list, rather than a GLOBAL `sidebar:*` key. So the entry
  // appears for precisely the callers the page can serve, with no new BE seed, and never
  // links to a page that would render empty for lack of permission. Same pattern as
  // Agency Approvals (its own AGENCY-route key). Deliberately NOT in AGENCY_LOCKED_PATHS: help
  // requests are the support channel, and none of that list's reasons (entities/clients the
  // agency cannot have yet) apply to asking for help.
  { to: '/help-requests' as const,  icon: LifeBuoy,        label: 'Help Requests' },
  { to: '/template-engine/templates' as const, icon: ClipboardList, label: 'Template Engine' },
  { to: '/reports' as const,                  icon: BarChart3,     label: 'Reports' },
  { to: '/favourites' as const,    icon: Star,            label: 'Favourites' },
];

const govNav = [
  // { to: '/risk' as const, icon: AlertTriangle, label: 'Risk & Decisions' }, // hidden for demo — restore when ready
  { to: '/org-chart' as const, icon: GitBranch, label: 'Org Chart' },
];

// const relationshipsNav = { to: '/relationships' as const, icon: Share2, label: 'Relationships' }; // hidden for demo — restore when ready
const clientsNav = { to: '/clients' as const, icon: Briefcase, label: 'Clients' };
const agenciesNav = { to: '/agencies' as const, icon: Building2, label: 'Agencies' };
// People directory, re-added after its ENT-89 retirement. ACL-gated to SA/AGENCY/CLIENT only
// (sidebar:people seeded NO for VENDOR/WORKER in acl-svc/config/ui-permissions.csv) — VENDOR and
// WORKER still reach person detail (/people/$personId) and Add Person (/people/new) via
// contextual deep-links, they just don't get a directory nav entry.
const peopleNav = { to: '/people' as const, icon: Contact, label: 'People' };
// U8: SA-only link to the platform-wide agency approval queue. Gated on its own
// AGENCY-route ACL key (agencies:approvals:read), not on ACL_KEY_BY_ROUTE/GLOBAL —
// this is a distinct admin surface from the persona-scoped `/agencies` list above,
// so it does not belong under `personaViewsEnabled` (that flag only governs
// persona-view list ordering, not ACL-gated admin queues). AGP01-03 exercises the
// queue by navigating to it directly, so passing docket runs could not have caught
// this nav entry being missing — see docs/tasks/u8-agency-approvals-nav.md.
const agencyApprovalsNav = { to: '/platform/agencies' as const, icon: ClipboardCheck, label: 'Agency Approvals' };

/**
 * C5 (pending) / C8 (declined) — while an agency's own approval is not `approved` (either still
 * `pending` or terminally `declined`), `ReadinessGate` shows a full-screen message in place of the
 * app — `AgencyActivationBoard`'s "Step N of 3" for pending, `AgencyPendingScreen` for declined —
 * but the Sidebar is ALREADY fully rendered and fully clickable alongside either one: `Sidebar`
 * mounts at the root layout, above `ReadinessGate` (src/routes/__root.tsx), so nothing previously
 * gated it. Every one of these areas operates on entities/clients/vendors/contracts the agency
 * cannot have in EITHER state, so they read as live but are dead ends. Locked here, in place, with
 * a reason — not hidden and not moved. Same locked path set for both states: a declined agency has
 * no more real data behind these areas than a pending one does — if anything less, since it will
 * never proceed.
 *
 * Kept meaningful and left live: Control Tower ('/', where the pending/declined message itself
 * renders), Documents (a declined agency may still need to retrieve records it already
 * submitted — and a pending one the one narrow exception `ReadinessGate` already carves a route
 * through, for the agreement itself), and Settings (account-level, not entity-level, and the
 * likeliest place either state's copy would point someone to contact support; rendered outside
 * these lists entirely, so it is untouched by construction). Integrations was the closest call for
 * the pending case — it's data-sync configuration rather than a live entity view, but with nothing
 * yet provisioned to sync it reads the same as the rest and is locked; flagging this one
 * specifically as the least clear-cut of the set.
 *
 * Scope: gated on `activePersona === 'AGENCY'` (the viewer IS the agency, in its own tenant
 * context) AND `approvalStatus` being `pending` or `declined` — the same signals `ReadinessGate`
 * already uses to decide what to show instead of the app, read from the same
 * `useAgencyStatus`/`useContextStore` sources so the two can't disagree. An SA cross-tenant-viewing
 * the same agency (e.g. via AgencyDetailPage) always has `active.persona === 'SA'`, never
 * `'AGENCY'` — so this can never fire for that view; see docs/tasks/c5-lock-onboarding-nav.md and
 * docs/tasks/c8-lock-declined-nav.md.
 */
const AGENCY_LOCKED_PATHS = new Set<string>([
  '/agent',
  '/entities',
  '/engagements',
  '/workforce',
  '/compliance',
  '/providers',
  '/invoices',
  '/integrations',
  '/template-engine/templates',
  '/reports',
  '/favourites',
  '/org-chart',
  '/agencies',
  '/clients',
  '/people',
]);

/**
 * C8 — the copy must read correctly from the reader's side of each state. A declined agency has
 * been refused, not left waiting; "available once onboarding is complete" would be false and
 * confusing for it (onboarding is over, not paused) — the same reason ReadinessGate itself never
 * reuses AgencyActivationBoard's copy for the declined branch.
 */
const AGENCY_LOCK_REASON: Record<'pending' | 'declined', string> = {
  pending: "Available once your agency's onboarding is complete.",
  declined: 'Unavailable — this agency application was declined.',
};

/**
 * C9 — codex-qa's finding on the C5/C8 review: before ANY context exists at all (the
 * "Setting up your workspace" window `ReadinessGate` shows via `BootLoader`/`OnboardingReadinessScreen`
 * — `ReadinessGate.tsx`'s own `!hasWorkspaces || !hasActiveContext` branch, which returns BEFORE the
 * agency fast-path or the SA fast-path ever run), the C5/C8 lock above fails OPEN: it keys on
 * `activePersona === 'AGENCY'`, and with no active context `activePersona` is `undefined` — not
 * `'AGENCY'` — so the AGENCY-specific gate simply never fires, and the nav renders fully live next
 * to a main content area that is not showing anyone's real app yet.
 *
 * This is NOT an agency-specific gap — it is universal. `ReadinessGate` applies the identical
 * `!hasWorkspaces || !hasActiveContext` branch to EVERY persona (CLIENT, AGENCY, SA, VENDOR,
 * WORKER) before any of their own fast-paths run, and it does so with NO pathname exception at
 * all (unlike the agency-pending branch's narrow `/agreements/$id` carve-out) — every route shows
 * the same loading screen regardless of which nav link was clicked. So unlike the C5/C8 states
 * (where the agency's own tenant genuinely exists, just pending/declined), NOTHING is scoped to a
 * real tenant yet here — not even Control Tower, Documents or Settings, the three areas C5/C8 kept
 * live. Every one of those, too, would just re-show the same loading screen if clicked. This locks
 * the ENTIRE nav, Settings included, with a neutral reason that doesn't presuppose which persona
 * this will turn out to be.
 *
 * Fails CLOSED, deliberately the opposite of C5/C8's own fail-open-while-loading choice: THAT
 * choice was safe because a real, already-known AGENCY was momentarily indistinguishable from an
 * approved one for one query round-trip. Here there is no known persona to fail open TOWARD — the
 * options are "lock" or "guess", and guessing which persona's exceptions to grant is exactly the
 * kind of fabrication this whole lock exists to avoid. This also matches the sidebar's own
 * pre-existing philosophy for uncertain permission state (`ACL_KEY_BY_ROUTE`'s docblock:
 * "Fail-closed: hidden while keys load / on error").
 *
 * Scope check: this uses the EXACT SAME fields (`memberships`, `active`) `ReadinessGate` reads for
 * its own `!hasWorkspaces || !hasActiveContext` branch, from the same store, so the two cannot
 * drift. Once `active` resolves, this clears — for an eventual SA that is a single query
 * round-trip (typically well under a second on a warm session, not the multi-second provisioning
 * wait a fresh self-signup sees), during which the main content is ALSO not their real dashboard
 * yet — so this does not regress the SA path's steady-state "fully live" behaviour, only the
 * instant before any persona is known at all. For an eventual AGENCY, the C5/C8 gate above takes
 * over correctly the moment `active`/`approvalStatus` resolve.
 */
const PRE_CONTEXT_LOCK_REASON = 'Available once your workspace is ready.';

function LockedNavItem({ icon: Icon, label, reason }: { icon: LucideIcon; label: string; reason: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className="flex items-center gap-2.5 px-4 py-[7px] text-sm font-medium text-sidebar-muted rounded-full cursor-not-allowed"
          aria-disabled="true"
          data-testid="sidebar-locked-item"
        >
          <Icon size={15} className="shrink-0" />
          {label}
          <Lock size={12} className="shrink-0 ml-auto" />
        </div>
      </TooltipTrigger>
      <TooltipContent side="right">{reason}</TooltipContent>
    </Tooltip>
  );
}

export function Sidebar() {
  const matchRoute = useMatchRoute();
  const theme = useDataTheme();
  // const orgRelationshipsEnabled = useOrgRelationshipsEnabled(); // hidden for demo — restore when ready
  const personaViewsEnabled = usePersonaViewsEnabled();
  // ENT-89 T15: UI permission-keys for the app shell (sidebar routing).
  const { checkPermission } = usePermissions(AclRoute.GLOBAL);
  // U8: separate ACL route — AgencyAclKeys.approvalsRead lives under AGENCY, not GLOBAL.
  const { checkPermission: checkAgencyPermission } = usePermissions(AclRoute.AGENCY);
  // CI-PHASE2: Help Requests is gated on its own route (see the mainNav entry).
  const { checkPermission: checkHelpRequestPermission } = usePermissions(AclRoute.HELP_REQUEST);
  // C9: same fields ReadinessGate reads for its OWN `!hasWorkspaces || !hasActiveContext` branch —
  // the universal pre-context window, before any persona (agency or otherwise) is known at all.
  const memberships = useContextStore((s) => s.memberships);
  const active = useContextStore((s) => s.active);
  const isPreContext = memberships.length === 0 || active === null;
  // C5/C8: same signals ReadinessGate reads to decide what to show instead of the app — shares its
  // query cache (same query keys), so this never drifts from what the main content area is
  // actually doing, and never fires for an SA cross-tenant view (persona stays 'SA').
  const activePersona = active?.persona;
  const isAgencyPersona = activePersona === 'AGENCY';
  const { approvalStatus, isLoading: approvalLoading } = useAgencyStatus({ enabled: isAgencyPersona });
  const agencyLockStatus: 'pending' | 'declined' | null =
    isAgencyPersona && !approvalLoading && (approvalStatus === 'pending' || approvalStatus === 'declined')
      ? approvalStatus
      : null;
  const isLockedAgency = agencyLockStatus !== null;
  // C9 takes precedence: it locks unconditionally (every path, Settings included), so it's checked
  // first. The C5/C8 gate only ever matters once C9 has already cleared (a persona is known).
  const lockReasonFor = (to: string): string | null => {
    if (isPreContext) return PRE_CONTEXT_LOCK_REASON;
    if (isLockedAgency && AGENCY_LOCKED_PATHS.has(to)) return AGENCY_LOCK_REASON[agencyLockStatus];
    return null;
  };
  // ENT-565: the Proposals entry needs the tenant's `crm_core` entitlement AND a proposals list key
  // (`useCrmAccess`). Hidden while either loads or fails (fail-closed, like the ACL gates).
  const { can: canCrm } = useCrmAccess();
  const inCrm = !!matchRoute({ to: '/crm', fuzzy: true });
  // ACL gate, layered AFTER the persona/flag logic below. No key ⇒ ungated.
  const aclAllows = (to: string) => {
    if (to === '/proposals') return canCrm(CrmAclAnyOf.proposalsList);
    if (to === '/help-requests') return checkHelpRequestPermission(HelpRequestAclKeys.listingRead);
    const key = ACL_KEY_BY_ROUTE[to];
    return !key || checkPermission(key);
  };
  // ENT-88: relationship console nav entry, behind the feature flag.
  // Team Members moved into Settings (post-ACL) — no nav entry here.
  const govItems = [
    ...govNav,
    // ...(orgRelationshipsEnabled ? [relationshipsNav] : []), // hidden for demo — restore when ready
    ...(checkAgencyPermission(AgencyAclKeys.approvalsRead) ? [agencyApprovalsNav] : []),
  ].filter((item) => aclAllows(item.to));
  // ENT-89 T15: nav visibility is ACL-driven only — the Clients/Agencies entries
  // are always present in the persona-views layout and the `aclAllows` filter
  // (sidebar:clients / sidebar:agencies) is the SINGLE source of truth for who
  // sees them. The old persona checks (Agencies = SA-only, Clients = AGENCY/SA)
  // were removed; gate via the `ui-permissions.csv` grants instead.
  // Order (per product): Dashboard, Agencies, Clients, Entities, Workers, Vendors, then the
  // remaining tabs. T0009 — "My Dashboard" (a separate, second item that used to sit here) is
  // RETIRED, not hidden: this Dashboard entry (renamed from "Control Tower") is now THE dashboard.
  const byPath = (to: string) => mainNav.find((i) => i.to === to)!;
  const promotedPaths = new Set(['/', '/agent', '/entities', '/import-center', '/workforce', '/providers']);
  const restNav = mainNav.filter((i) => !promotedPaths.has(i.to));
  const mainItems = (personaViewsEnabled
    ? [
        byPath('/'),
        byPath('/agent'),
        agenciesNav,
        clientsNav,
        byPath('/entities'),
        byPath('/import-center'),
        byPath('/workforce'),
        peopleNav,
        byPath('/providers'),
        ...restNav,
      ]
    : mainNav
  ).filter((item) => aclAllows(item.to));

  function isActive(to: string) {
    if (to === '/') return !!matchRoute({ to: '/', fuzzy: false });
    return !!matchRoute({ to, fuzzy: true });
  }

  const linkBase = 'flex items-center gap-2.5 px-4 py-[7px] text-sm font-medium text-sidebar-foreground hover:text-text hover:bg-surface-muted/60 transition-colors rounded-full';
  const linkActive = 'sidebar-active flex items-center gap-2.5 px-4 py-[7px] text-sm font-semibold rounded-full';

  return (
    <aside className="fixed left-0 top-0 h-screen w-[220px] flex flex-col z-50 bg-sidebar">
      {/* Logo */}
      <div className="h-14 flex items-center px-5 shrink-0">
        <img
          src={theme === 'dark' ? '/brand/entiti-wordmark-dark.png' : '/brand/entiti-wordmark.png'}
          alt="Entiti"
          width={61}
          height={24}
          className="h-6 w-auto"
        />
      </div>

      {/* Nav — inside the CRM tool the CRM's own navigation replaces the workspace's. */}
      {inCrm ? (
        <CrmSidebarNav linkBase={linkBase} linkActive={linkActive} />
      ) : (
      <nav className="flex-1 py-3 overflow-y-auto">
        <div className="px-2.5 space-y-0.5">
          {mainItems.map(({ to, icon: Icon, label }) => {
            const reason = lockReasonFor(to);
            return reason ? (
              <LockedNavItem key={to} icon={Icon} label={label} reason={reason} />
            ) : (
              <Link key={to} to={to} className={isActive(to) ? linkActive : linkBase}>
                <Icon size={15} className="shrink-0" />
                {label}
              </Link>
            );
          })}
        </div>

        <div className="mt-6 px-2.5 space-y-0.5">
          <Text variant="label-sm" as="div" className="px-4 mb-2 text-sidebar-muted">
            Governance
          </Text>
          {govItems.map(({ to, icon: Icon, label }) => {
            const reason = lockReasonFor(to);
            return reason ? (
              <LockedNavItem key={to} icon={Icon} label={label} reason={reason} />
            ) : (
              <Link key={to} to={to} className={isActive(to) ? linkActive : linkBase}>
                <Icon size={15} className="shrink-0" />
                {label}
              </Link>
            );
          })}
        </div>
      </nav>
      )}

      {/* Bottom */}
      <div className="px-2.5 py-4 shrink-0">
        {/* C9: Settings is outside the mapped arrays above, so the pre-context lock is applied to
            it explicitly here — it is not exempt the way it is under C5/C8 (see C9's own comment:
            nothing is scoped to a real tenant yet in this window, Settings included). */}
        {isPreContext ? (
          <LockedNavItem icon={Settings} label="Settings" reason={PRE_CONTEXT_LOCK_REASON} />
        ) : (
          <Link to="/settings" className={isActive('/settings') ? linkActive : linkBase}>
            <Settings size={15} className="shrink-0" />
            Settings
          </Link>
        )}
      </div>
    </aside>
  );
}
